/**
 * Copyright 2024-2025 Optimizely
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { LogLevelToLower } from './logging/logger';
import { resolvablePromise } from "./utils/promise/resolvablePromise";
export const SERVICE_FAILED_TO_START = '%s failed to start, reason: %s';
export const SERVICE_STOPPED_BEFORE_RUNNING = '%s stopped before running';
/**
 * The service interface represents an object with an operational state,
 * with methods to start and stop. The design of this interface in modelled
 * after Guava Service interface (https://github.com/google/guava/wiki/ServiceExplained).
 */
export var ServiceState;
(function (ServiceState) {
    ServiceState[ServiceState["New"] = 0] = "New";
    ServiceState[ServiceState["Starting"] = 1] = "Starting";
    ServiceState[ServiceState["Running"] = 2] = "Running";
    ServiceState[ServiceState["Stopping"] = 3] = "Stopping";
    ServiceState[ServiceState["Terminated"] = 4] = "Terminated";
    ServiceState[ServiceState["Failed"] = 5] = "Failed";
})(ServiceState || (ServiceState = {}));
export class BaseService {
    constructor(startupLogs = []) {
        this.disposable = false;
        this.state = ServiceState.New;
        this.startPromise = resolvablePromise();
        this.stopPromise = resolvablePromise();
        this.startupLogs = startupLogs;
        // avoid unhandled promise rejection
        this.startPromise.promise.catch(() => { });
        this.stopPromise.promise.catch(() => { });
    }
    makeDisposable() {
        this.disposable = true;
    }
    setLogger(logger) {
        this.logger = logger;
    }
    printStartupLogs() {
        if (!this.logger) {
            return;
        }
        for (const { level, message, params } of this.startupLogs) {
            const methodName = LogLevelToLower[level];
            const method = this.logger[methodName];
            method.call(this.logger, message, ...params);
        }
    }
    onRunning() {
        return this.startPromise.promise;
    }
    onTerminated() {
        return this.stopPromise.promise;
    }
    getState() {
        return this.state;
    }
    isStarting() {
        return this.state === ServiceState.Starting;
    }
    isRunning() {
        return this.state === ServiceState.Running;
    }
    isNew() {
        return this.state === ServiceState.New;
    }
    isDone() {
        return [
            ServiceState.Stopping,
            ServiceState.Terminated,
            ServiceState.Failed
        ].includes(this.state);
    }
    start() {
        this.printStartupLogs();
    }
}
export const __platforms = ['__universal__'];
//# sourceMappingURL=service.js.map